import { useState, useCallback } from 'react'
import { ErpTemple } from '@ca/common'
import EmptyResult from '@ui/components/EmptyResult'
import Input from '@ui/forms/Input'
import { useTempleActions } from 'hooks/temple/useTemples'
import { trpc } from 'api'
import { router } from 'expo-router'
import { useTranslation } from 'react-i18next'

export default function TempleSelectSearch() {
  const { t } = useTranslation(['ui', 'frame'])
  const text = 'frame:temple.select'
  const inputText = 'frame:temple.search.input'

  const [localQuery, setLocalQuery] = useState('')
  const [foundTemple, setFoundTemple] = useState<ErpTemple | null>(null)

  const query = trpc.temples.list.useQuery(
    { query: localQuery, limit: 2 },
    {
      placeholderData: (v) => v,
      refetchOnWindowFocus: false,
      enabled: false,
      refetchOnMount: false,
      retryOnMount: false,
      retry: false,
      refetchOnReconnect: false,
      networkMode: 'online',
    }
  )

  const showLoading = query.isFetching || query.isRefetching

  const { saveTemple } = useTempleActions()

  const handleResult = useCallback(
    (data: ErpTemple[]) => {
      if (data.length === 1) {
        setFoundTemple(data[0])
        return
      }
      setFoundTemple(null)
      router.navigate(`/frame/temple/search?q=${localQuery}`)
    },
    [localQuery]
  )

  const onSearch = useCallback(async () => {
    setFoundTemple(null)
    const res = await query.refetch()
    handleResult(res.data?.data || [])
  }, [handleResult, query])

  const onSave = useCallback(async () => {
    if (!foundTemple) return
    await saveTemple(foundTemple)
  }, [foundTemple, saveTemple])

  const onChangeText = useCallback((text: string) => {
    setLocalQuery(text)
    setFoundTemple(null)
  }, [])

  return (
    <EmptyResult
      disableAnimation
      title={t(`${text}.empty.saved.title`)}
      desc={t(`${text}.empty.saved.desc`)}
      button={
        foundTemple
          ? {
              loading: showLoading,
              label: t('ui:saveTemple'),
              iconEnd: 'arrow-narrow-right',
              onPress: onSave,
            }
          : {
              disabled: !localQuery.trim(),
              loading: showLoading,
              label: t('ui:search'),
              iconEnd: 'search',
              onPress: onSearch,
            }
      }
    >
      <Input
        label={t(`${inputText}.label`)}
        placeholder={t(`${inputText}.placeholder`)}
        value={foundTemple ? foundTemple.name : localQuery}
        onChangeText={foundTemple ? undefined : onChangeText}
        readonly={!!foundTemple}
        isSuccess={!!foundTemple}
        autoCorrect={false}
      />
    </EmptyResult>
  )
}
